/* eslint-disable no-undef */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import DashboardLayout from "./DashboardLayout";
import ResumeFiltersModal from "../../components/ResumeFiltersModal";
import { trackPageView, trackButtonClick } from "../../utils/analytics";
import { 
  FileText, 
  FileCheck2, 
  Mail, 
  Search, 
  Upload, 
  Plus, 
  Download, 
  Trash2, 
  CheckCircle2, 
  History, 
  Zap, 
  AlertCircle,
  RefreshCw,
  Clock,
  ExternalLink,
  Target,
  Sparkles,
  Cpu,
  ShieldCheck,
  ChevronRight,
  ChevronDown
} from "lucide-react";

const Dashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [matchResult, setMatchResult] = useState(null);
  const [appQuestion, setAppQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [tailored, setTailored] = useState("");
  const [tlError, setTlError] = useState("");
  const [tailoredHistory, setTailoredHistory] = useState([]);
  const [matchHistory, setMatchHistory] = useState([]);
  const [letters, setLetters] = useState([]);
  const [tab, setTab] = useState(location.state?.tab || "builder"); 
  const [resumes, setResumes] = useState([]);
  const [selectedResumeId, setSelectedResumeId] = useState("");
  const [newResumeFile, setNewResumeFile] = useState(null);
  const [newResumeTitle, setNewResumeTitle] = useState("");
  const [resumeUploading, setResumeUploading] = useState(false);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [selectedResumeForFilters, setSelectedResumeForFilters] = useState(null);
  
  // Loading states for AI actions
  const [matchLoading, setMatchLoading] = useState(false);
  const [tailoringLoading, setTailoringLoading] = useState(false);
  const [letterLoading, setLetterLoading] = useState(false);

  const loadResumes = async () => {
    try {
      const r = await axiosInstance.get("/resumes");
      const items = (r.data && r.data.items) || [];
      setResumes(items);
      if (!selectedResumeId && items.length > 0) {
        const active = items.find((it) => it.is_active);
        setSelectedResumeId(active ? active.id : items[0].id);
      }
    } catch (e) {}
  };

  const loadHistory = async () => {
      try {
          const [r1, r2] = await Promise.all([
              axiosInstance.get("/tailored_resumes").catch(() => ({ data: { items: [] } })),
              axiosInstance.get("/match/history").catch(() => ({ data: { items: [] } }))
          ]);
          setTailoredHistory(r1.data.items || []);
          setMatchHistory(r2.data.items || []);
      } catch (e) {}
  };

  const handleManageFilters = (resume) => {
    setSelectedResumeForFilters(resume);
    setFiltersModalOpen(true);
  };

  const handleFiltersUpdate = () => loadResumes();

  useEffect(() => {
    trackPageView("/dashboard", "Dashboard Overview");
    const token = localStorage.getItem("token");
    if (token) window.postMessage({ source: "applyease", action: "AddToken", token }, "*");

    const getData = () => {
      setError("");
      setLoading(true);
      axiosInstance.get("/user")
        .then(response => setUserData(response.data))
        .catch(e => setError(e?.response?.data?.detail || "Failed to load identity"))
        .finally(() => setLoading(false));
    };
    getData();
    try {
      const p = new URLSearchParams(window.location.search);
      const jd = p.get("jd");
      if (jd) setJobDescription(jd);
    } catch {}
    loadHistory();
    axiosInstance.get("/cover_letters").then(r => setLetters(r.data.items || [])).catch(() => {});
    loadResumes();
  }, []);

  useEffect(() => {
    if (location.state?.tab) setTab(location.state.tab);
  }, [location.state]);

  const handleUploadNewResume = async () => {
    if (!newResumeFile) return setTlError("Asset selection required.");
    trackButtonClick("Upload Resume", "Dashboard Resumes");
    setResumeUploading(true);
    setTlError("");
    try {
      const formData = new FormData();
      formData.append("resume", newResumeFile);
      if (newResumeTitle.trim()) formData.append("title", newResumeTitle.trim().toUpperCase());
      formData.append("make_active", "true");
      const resp = await axiosInstance.post("/resumes/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setNewResumeFile(null); setNewResumeTitle("");
      await loadResumes();
      if (resp.data?.id) setSelectedResumeId(resp.data.id);
    } catch (e) { setTlError("Asset injection failure."); } finally { setResumeUploading(false); }
  };

  const handleActivateResume = async (resumeId) => {
    trackButtonClick("Activate Resume", "Dashboard Resumes");
    try {
      await axiosInstance.post("/resumes/activate", { resume_id: resumeId });
      await loadResumes();
      setSelectedResumeId(resumeId);
    } catch (e) { setTlError("Activation failure."); }
  };

  const handleDeleteResume = async (resume) => {
    if (resume.is_active) {
      setTlError("Cannot purge primary asset. Please designate another resume as primary first.");
      return;
    }
    if (!window.confirm("Purge this tactical asset?")) return;
    trackButtonClick("Delete Resume", "Dashboard Resumes");
    try {
      await axiosInstance.delete(`/resumes/${resume.id}`);
      await loadResumes();
      if (selectedResumeId === resume.id) setSelectedResumeId("");
    } catch (e) { setTlError("Purge failure."); }
  };

  const handleDownloadResumeById = async (resumeId, filename) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_API_BASE || "http://localhost:8000"}/resumes/${resumeId}/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename || "asset.pdf"; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {}
  };

  const handleDownloadTailored = async (id, filename) => {
    try {
       const token = localStorage.getItem("token");
       const resp = await fetch(`${process.env.REACT_APP_API_BASE || "http://localhost:8000"}/tailored_resume_download?id=${id}`, {
          headers: { Authorization: `Bearer ${token}` }
       });
       if (!resp.ok) throw new Error("Download failed");
       const blob = await resp.blob();
       const url = URL.createObjectURL(blob);
       const a = document.createElement("a"); a.href = url; a.download = filename || "tailored_resume.pdf"; 
       document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) {
       setTlError("Download failed.");
    }
  };

  const handleMatch = async () => {
    trackButtonClick("Compute Match", "Dashboard Builder");
    if (!jobDescription.trim()) return;
    setMatchLoading(true);
    try {
      const payload = { jobDescription };
      if (selectedResumeId) payload.resume_id = selectedResumeId;
      const resp = await axiosInstance.post("/match", payload);
      setMatchResult(resp.data);
      loadHistory();
    } catch (e) {
      setTlError("Match computation failed.");
    } finally {
      setMatchLoading(false);
    }
  };

  const handleTailored = async () => {
    setTlError("");
    trackButtonClick("Generate Tailored CV", "Dashboard Builder");
    if (!jobDescription.trim()) return setTlError("JD input required for neural tailoring.");
    setTailoringLoading(true);
    try {
      const resp = await axiosInstance.post("/tailored_resume", { jobDescription, save: true });
      setTailored(resp.data.resume_text || "");
      if (!matchResult) setMatchResult({ percent: 0, matchingWords: resp.data.matching_words || [], missingWords: resp.data.missing_words || [] });
      loadHistory();
    } catch (e) { setTlError("AI tailoring failed."); }
    finally { setTailoringLoading(false); }
  };

  const handleGenerateCoverLetter = async () => {
    setTlError("");
    if (!jobDescription.trim()) return setTlError("JD input required.");
    setLetterLoading(true);
    try {
      const payload = { job_description: jobDescription, filename: "cover_letter.pdf", save: true, use_llm: true };
      if (selectedResumeId) payload.resume_id = selectedResumeId;
      const resp = await axiosInstance.post("/cover_letters/generate", payload, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: resp.headers['content-type'] || "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "cover_letter.pdf"; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      const r = await axiosInstance.get('/cover_letters'); setLetters(r.data.items || []);
    } catch (e) { setTlError("Letter generation failed."); }
    finally { setLetterLoading(false); }
  };


  const handleCustomAnswer = async () => {
    setAnswer("");
    setTlError("");
    trackButtonClick("Generate Custom Answer", "Dashboard Interview Helper");
    if (!appQuestion.trim()) return setTlError("Please enter a question.");
    if (!jobDescription.trim()) return setTlError("Please enter a job description first.");
    try {
      const payload = { 
        applicationQuestion: appQuestion,
        jobDescription: jobDescription
      };
      const resp = await axiosInstance.post("/custom-answer", payload);
      setAnswer(resp.data.answer || resp.data.response || "");
    } catch (e) { 
      setTlError("Failed to generate answer."); 
    }
  };


  const getPercentageColor = (percent) => {
    if (percent >= 80) return "text-emerald-500 bg-emerald-50 border-emerald-100";
    if (percent >= 50) return "text-amber-500 bg-amber-50 border-amber-100";
    return "text-rose-500 bg-rose-50 border-rose-100";
  };

  return (
    <DashboardLayout activeTab={tab} setTab={setTab} userData={userData} matchResult={matchResult}>
      <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-10 font-sans">
        
        {/* Navigation Tabs - Modern Segmented Control */}
        <div className="flex bg-white p-0.5 md:p-1 lg:p-1.5 rounded-xl md:rounded-2xl lg:rounded-[2rem] border border-slate-200 shadow-xl shadow-blue-500/5 w-full md:w-fit overflow-x-auto scrollbar-hide">
           {[
             { id: 'builder', label: 'CV Matcher', icon: Zap },
             { id: 'resumes', label: 'My Resumes', icon: FileCheck2 },
             { id: 'letters', label: 'Cover Letters', icon: Mail }
           ].map(t => (
             <button
               key={t.id}
               onClick={() => setTab(t.id)}

               className={`flex items-center gap-1.5 md:gap-2 lg:gap-3 px-3 md:px-4 lg:px-6 py-1.5 md:py-2 lg:py-2.5 rounded-lg md:rounded-xl text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-all italic whitespace-nowrap shrink-0 ${
                 tab === t.id 
                  ? 'bg-blue-600 text-white shadow-2xl shadow-blue-500/20' 
                  : 'text-slate-400 hover:text-slate-900 hover:bg-slate-50'
               }`}
             >
               <t.icon size={12} className={`md:w-3.5 md:h-3.5 lg:w-4 lg:h-4 ${tab === t.id ? 'fill-white' : ''}`} />
               {t.label}
             </button>
           ))}
        </div>

        {error && (
          <div className="p-4 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3 text-red-700 font-bold animate-in slide-in-from-top-2">
            <AlertCircle className="shrink-0" size={18} />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {tab === 'builder' && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8 space-y-8">
              <div className="bg-white border border-slate-200 rounded-[2.5rem] p-8 shadow-sm relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none -rotate-12 group-hover:rotate-0 transition-all duration-1000"><Target size={120} /></div>
                
                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-3 italic">
                   <Target size={16} className="text-blue-600" /> Job Analysis
                </h3>

                <div className="space-y-8">
                  <div className="space-y-2">
                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Select Resume</label>
                    <div className="relative">
                      <select
                        className="w-full pl-4 pr-10 py-3 bg-slate-50 border border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-700 appearance-none outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all border-b-4 italic cursor-pointer"
                        value={selectedResumeId}
                        onChange={(e) => setSelectedResumeId(e.target.value)}
                      >
                        <option value="">Use Active Resume</option>
                        {resumes.map((r) => (
                          <option key={r.id} value={r.id}>{r.title || r.filename} {r.is_active ? "★" : ""}</option>
                        ))}
                      </select>
                      <ChevronDown size={18} className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Job Description</label>
                    <textarea 
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 min-h-[200px] transition-all resize-none border-b-4 shadow-inner italic leading-relaxed" 
                      placeholder="Paste the job description here to analyze match score and missing keywords..." 
                      value={jobDescription} 
                      onChange={(e) => setJobDescription(e.target.value)} 
                    />
                  </div>
                  
                  <div className="flex flex-wrap gap-3 pt-4">
                    <button 
                      className="px-6 py-3 bg-blue-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-700 active:scale-95 shadow-2xl shadow-blue-500/20 transition-all flex items-center gap-3 italic disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleMatch}
                      disabled={matchLoading || tailoringLoading || letterLoading}
                    >
                      {matchLoading ? <RefreshCw className="animate-spin" size={16} /> : <Zap size={16} className="fill-white" />}
                      {matchLoading ? "Computing..." : "Compute Match"}
                    </button>
                    <button 
                      className="px-6 py-3 bg-white border-2 border-slate-200 text-slate-600 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 hover:border-slate-400 active:scale-95 transition-all flex items-center gap-3 italic disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleTailored}
                      disabled={matchLoading || tailoringLoading || letterLoading}
                    >
                      {tailoringLoading ? <RefreshCw className="animate-spin" size={16} /> : <Sparkles size={16} className="text-blue-500" />}
                      {tailoringLoading ? "Tailoring..." : "AI Tailoring"}
                    </button>
                    <button 
                      className="px-6 py-3 bg-white border-2 border-slate-200 text-slate-600 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 hover:border-slate-400 active:scale-95 transition-all flex items-center gap-3 italic disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleGenerateCoverLetter}
                      disabled={matchLoading || tailoringLoading || letterLoading}
                    >
                      {letterLoading ? <RefreshCw className="animate-spin" size={16} /> : <Mail size={16} className="text-blue-500" />}
                      {letterLoading ? "Generating..." : "Generate Letter"}
                    </button>
                  </div>
                </div>

                {tlError && (
                  <div className="mt-8 p-5 bg-rose-50 border border-rose-100 rounded-2xl text-rose-600 text-[10px] font-black uppercase tracking-widest italic flex items-center gap-3">
                     <AlertCircle size={16} /> {tlError}
                  </div>
                )}
                
                {tailored && (
                  <div className="mt-8 pt-6 border-t border-slate-100 animate-in slide-in-from-bottom-8 duration-700">
                    <div className="flex items-center justify-between mb-6">
                       <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest italic flex items-center gap-2"><Sparkles size={16} className="text-blue-500" /> AI-Generated Resume</h4>
                       <div className="flex gap-3">
                          <button onClick={async () => {
                            try {
                              await axiosInstance.post("/tailored_resume_pdf", { jobDescription, save: true }, { responseType: "blob" });
                              loadHistory();
                              setTlError("SCHEMA STORED IN HISTORY"); setTimeout(() => setTlError(""), 3000);
                            } catch (e) { setTlError("STORE FAILURE"); }
                          }} className="px-5 py-2.5 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-600 hover:text-white transition-all font-black text-[9px] uppercase tracking-widest italic border border-blue-100 shadow-sm shadow-blue-500/5">Store to Vault</button>
                          <button onClick={() => {}} className="p-2.5 bg-slate-900 text-white rounded-xl hover:bg-black transition-all shadow-lg"><Download size={18} /></button>
                       </div>
                    </div>
                    <div className="p-6 bg-slate-900 rounded-[2rem] overflow-hidden relative group border border-slate-800 shadow-2xl">
                       <pre className="text-xs text-blue-100/70 font-mono whitespace-pre-wrap max-h-[400px] overflow-auto scrollbar-thin scrollbar-thumb-white/10 pr-6 italic leading-relaxed">
                          {tailored}
                       </pre>
                       <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent pointer-events-none opacity-40" />
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white border border-slate-200 rounded-[2.5rem] p-8 shadow-sm relative overflow-hidden group">
                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-3 italic">
                   <Plus size={22} className="text-blue-600" /> Interview Helper
                </h3>
                <div className="space-y-8">
                  <div className="space-y-2">
                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Mission Query (Question)</label>
                    <textarea 
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 min-h-[120px] transition-all resize-none border-b-4 italic shadow-inner" 
                      placeholder="e.g., 'Describe your methodology for leading tactical deployments under critical fault conditions?'" 
                      value={appQuestion} 
                      onChange={(e) => setAppQuestion(e.target.value)} 
                    />
                  </div>
                  <button className="px-6 py-3 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-black active:scale-95 shadow-xl shadow-slate-200 transition-all italic" onClick={handleCustomAnswer}>Generate Answer</button>
                  
                  {answer && (
                    <div className="space-y-6 mt-10 animate-in fade-in duration-500">
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 italic flex items-center gap-2"><Cpu size={12}/> Optimized Strategy</label>
                      <div className="p-6 bg-blue-50/50 border border-blue-100 rounded-[2rem] text-xs text-slate-800 font-bold italic relative overflow-hidden shadow-inner leading-relaxed">
                         <div className="relative z-10">{answer}</div>
                         <div className="absolute top-0 right-0 p-10 opacity-5 rotate-12 -mr-10"><Mail size={80} /></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="lg:col-span-4 space-y-8">
              {/* Match Result Analysis (Move from center to right) */}
              {matchResult && (
                <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-6 shadow-2xl animate-in slide-in-from-top-4 duration-500 relative overflow-hidden group">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-blue-600/10 rounded-full -mr-16 -mt-16 blur-2xl group-hover:bg-blue-600/20 transition-all" />
                  
                  <div className="flex items-center justify-between mb-6 relative z-10">
                     <div>
                        <div className="text-[9px] font-black text-blue-400 uppercase tracking-widest mb-1 italic">Active Match</div>
                        <h4 className="text-xl font-black text-white tracking-tight italic">Detailed Analysis</h4>
                     </div>
                     <button 
                        onClick={() => setMatchResult(null)}
                        className="p-2 text-slate-500 hover:text-white hover:bg-white/10 rounded-xl transition-all"
                     >
                        <Plus size={18} className="rotate-45" />
                     </button>
                  </div>

                  <div className="flex items-center gap-4 mb-8 relative z-10">
                     <div className={`p-4 rounded-2xl border-2 text-3xl font-black shadow-2xl italic ${getPercentageColor(matchResult.percent)} bg-white/5`}>
                        {Math.round(matchResult.percent)}%
                     </div>
                     <div className="flex flex-col">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest italic">Compatibility Score</span>
                        <div className="flex gap-1 mt-1">
                           {[1,2,3,4,5].map(i => (
                              <div key={i} className={`w-3 h-1 rounded-full ${i <= (matchResult.percent/20) ? 'bg-blue-500' : 'bg-slate-800'}`} />
                           ))}
                        </div>
                     </div>
                  </div>

                  <div className="space-y-6 relative z-10">
                    <div className="space-y-3">
                      <label className="text-[9px] font-black text-emerald-400 uppercase tracking-widest flex items-center gap-2 italic">
                         <CheckCircle2 size={12} /> Matching Keywords
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {(matchResult.matchingWords || []).length > 0 ? (matchResult.matchingWords || []).map((w, i) => (
                          <span className="px-2.5 py-1 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[8px] rounded-lg font-black uppercase tracking-tighter" key={`m-${i}`}>{w}</span>
                        )) : <span className="text-[10px] text-slate-500 font-bold italic uppercase">None Found</span>}
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <label className="text-[9px] font-black text-blue-400 uppercase tracking-widest flex items-center gap-2 italic">
                         <Cpu size={12} /> Missing Keywords
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {(matchResult.missingWords || []).length > 0 ? (matchResult.missingWords || []).map((w, i) => (
                          <span className="px-2.5 py-1 bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[8px] rounded-lg font-black uppercase tracking-tighter" key={`x-${i}`}>{w}</span>
                        )) : <span className="text-[10px] text-emerald-400 font-black italic uppercase tracking-widest">Full Calibration</span>}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Match History */}
              <div className="bg-white border border-slate-200 rounded-[2.5rem] p-6 shadow-sm h-fit overflow-hidden relative">
                 <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2 mb-6 z-10 relative italic">
                    <Target size={16} className="text-emerald-500" /> Recent Matches
                 </h3>
                 {matchHistory.length === 0 ? (
                   <div className="text-center py-12 px-6 border-2 border-dashed border-slate-100 rounded-[2rem]">
                     <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em] italic">No Match Data</p>
                   </div>
                 ) : (
                   <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 scrollbar-thin">
                     {matchHistory.slice(0, 10).map((h) => (
                       <div 
                         key={h.id} 
                         onClick={() => {
                           setMatchResult({
                             percent: h.percent,
                             matchingWords: h.matchingWords,
                             missingWords: h.missingWords
                           });
                           setJobDescription(h.jobDescription);
                           if (h.resume_id) setSelectedResumeId(h.resume_id);
                           window.scrollTo({ top: 0, behavior: 'smooth' });
                         }}
                         className="p-5 bg-slate-50 border border-slate-100 rounded-[2rem] hover:bg-white hover:border-emerald-200 transition-all cursor-pointer shadow-sm group"
                       >
                          <div className="flex justify-between items-center mb-2">
                             <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest italic">{new Date(h.created_at).toLocaleDateString()}</div>
                             <div className={`px-2 py-1 rounded-lg text-[9px] font-black ${h.percent >= 80 ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
                               {Math.round(h.percent)}%
                             </div>
                          </div>
                          <div className="text-[10px] font-bold text-slate-700 line-clamp-2 italic leading-relaxed group-hover:text-blue-600 transition-colors">
                            {h.jobDescription}
                          </div>
                       </div>
                     ))}
                   </div>
                 )}
              </div>

              {/* Tailored History */}
              <div className="bg-white border border-slate-200 rounded-[2.5rem] p-6 shadow-sm h-fit overflow-hidden relative">
                <div className="absolute top-0 right-0 w-40 h-40 bg-blue-50 rounded-full -mr-20 -mt-20 opacity-50 z-0" />
                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2 mb-6 z-10 relative italic">
                   <History size={16} className="text-blue-500" /> Tailored CVs
                </h3>
                {tailoredHistory.length === 0 ? (
                  <div className="text-center py-24 px-6 border-2 border-dashed border-slate-100 rounded-[2.5rem] z-10 relative">
                    <History className="mx-auto text-slate-100 mb-6" size={48} />
                    <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em] italic">No Tailored Data</p>
                  </div>
                ) : (
                  <div className="space-y-5 z-10 relative max-h-[400px] overflow-y-auto pr-2 scrollbar-thin">
                    {tailoredHistory.slice(0, 15).map((h) => (
                      <div 
                        key={h.id} 
                        onClick={() => {
                          setTailored(h.resume_text || "");
                          setJobDescription(h.jobDescription || "");
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="p-4 bg-slate-50/50 border border-slate-100 rounded-2xl group hover:border-blue-300 hover:bg-white transition-all shadow-sm hover:shadow-xl hover:shadow-blue-500/5 cursor-pointer"
                      >
                        <div className="text-[9px] font-black text-blue-500 uppercase tracking-widest flex items-center gap-2 mb-3 italic">
                           <Clock size={12} /> {new Date(h.created_at).toLocaleDateString()}
                        </div>
                        <div className="text-[11px] font-black text-slate-900 truncate pr-6 italic uppercase tracking-tight group-hover:text-blue-600 transition-colors">{h.filename?.replace('.pdf', '') || 'TAILORED RESUME'}</div>
                        <div className="flex gap-2 mt-6 opacity-0 group-hover:opacity-100 transition-all translate-y-2 group-hover:translate-y-0">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadTailored(h.id, h.filename);
                            }} 
                            className="flex-1 py-2 bg-slate-900 text-white rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-black transition-all italic flex items-center justify-center gap-2"
                          >
                            Download <Download size={12}/>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === 'resumes' && (
          <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
             <div className="bg-white border border-slate-200 rounded-[2.5rem] p-8 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none -rotate-12"><FileCheck2 size={120}/ ></div>
                <div className="flex items-center justify-between mb-8 relative z-10">
                   <div>
                      <h3 className="text-3xl font-black text-slate-900 tracking-tight italic flex items-center gap-3">
                        <FileCheck2 size={32} className="text-blue-600" /> Resume Management
                      </h3>
                      <p className="text-slate-400 text-xs font-black uppercase tracking-widest mt-2">Managing {resumes.length} Resumes</p>
                   </div>
                   <button onClick={loadResumes} className="w-12 h-12 rounded-2xl bg-slate-50 text-slate-400 hover:text-blue-600 border border-slate-100 flex items-center justify-center transition-all hover:bg-white shadow-inner"><RefreshCw size={22} /></button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 relative z-10">
                   <div className="lg:col-span-4">
                      <div className="p-6 bg-slate-900 text-white rounded-[2rem] sticky top-24 shadow-2xl shadow-blue-900/10 border border-slate-800">
                         <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-[0.2em] mb-6 flex items-center gap-3 italic">
                            <Upload size={16} /> Upload Resume
                         </h4>
                         <div className="space-y-8">
                             <div className="space-y-2">
                               <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1">Resume Label</label>
                               <input
                                 className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-[10px] font-black uppercase tracking-widest outline-none focus:bg-white/10 focus:border-blue-500 transition-all border-b-4 italic shadow-inner text-white"
                                 type="text"
                                 placeholder="E.G. SOFTWARE ENGINEER 2024"
                                 value={newResumeTitle}
                                 onChange={(e) => setNewResumeTitle(e.target.value)}
                               />
                             </div>
                             <div className="space-y-2">
                                <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1">File Selection</label>
                                <div className="relative group">
                                   <input className="hidden" id="dashboard-resume-upload" type="file" accept=".pdf,.doc,.docx" onChange={(e) => setNewResumeFile(e.target.files?.[0] || null)} />
                                   <label htmlFor="dashboard-resume-upload" className="flex flex-col items-center justify-center p-6 bg-white/5 border-2 border-dashed border-white/10 rounded-xl hover:border-blue-500 hover:bg-white/10 transition-all cursor-pointer group/upload">
                                      <Upload size={32} className="text-blue-500 mb-4 group-hover/upload:scale-110 transition-transform" />
                                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest truncate max-w-full px-4 italic">
                                         {newResumeFile ? newResumeFile.name : 'Choose File'}
                                      </span>
                                   </label>
                                </div>
                             </div>
                             <button
                               className="w-full py-3 bg-blue-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-700 disabled:opacity-30 shadow-2xl shadow-blue-600/20 transition-all active:scale-95 italic flex items-center justify-center gap-3"
                               onClick={handleUploadNewResume}
                               disabled={resumeUploading || !newResumeFile}
                             >
                               {resumeUploading ? "Uploading..." : "Upload Resume"}
                             </button>
                         </div>
                      </div>
                   </div>

                   <div className="lg:col-span-8 space-y-6">
                      {tlError && <div className="p-5 bg-rose-50 border border-rose-100 rounded-2xl text-rose-600 text-[10px] font-black uppercase tracking-widest italic">{tlError}</div>}
                      {resumes.length === 0 ? (
                         <div className="py-20 text-center border-4 border-dashed border-slate-50 rounded-[2rem] bg-slate-50/20">
                             <Target size={48} className="mx-auto text-slate-100 mb-6" />
                             <p className="text-xs font-black text-slate-300 uppercase tracking-[0.3em] italic">No resumes uploaded</p>
                         </div>
                      ) : (
                          resumes.map((r) => (
                            <div
                              key={r.id}
                              className={`flex flex-col md:flex-row items-center justify-between p-6 rounded-[2rem] border-2 transition-all duration-500 group relative overflow-hidden ${
                                r.is_active 
                                 ? 'bg-emerald-50/30 border-emerald-400 shadow-2xl shadow-emerald-500/10' 
                                 : 'bg-white border-slate-100 hover:border-blue-400 hover:shadow-2xl hover:shadow-blue-500/5'
                              }`}
                            >
                              <div className="flex-1 flex items-center gap-5 min-w-0 w-full">
                                 <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-500 shadow-inner shrink-0 ${
                                    r.is_active ? 'bg-emerald-500 text-white' : 'bg-slate-50 text-slate-300 group-hover:bg-blue-600 group-hover:text-white'
                                 }`}>
                                    <FileText size={24} />
                                 </div>
                                 <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-3 mb-1 min-w-0 flex-wrap">
                                       <span className="text-lg font-black text-slate-900 italic tracking-tight uppercase truncate max-w-full">
                                          {r.title || r.filename}
                                       </span>
                                       {r.is_active && (
                                          <div className="flex items-center gap-1.5 px-3 py-1 bg-emerald-500 text-white text-[9px] font-black uppercase rounded-full shadow-lg shadow-emerald-200 italic tracking-widest animate-in slide-in-from-left-4 shrink-0">
                                             <ShieldCheck size={10}/> Primary
                                          </div>
                                       )}
                                    </div>
                                    <div className="flex items-center gap-4 text-[9px] font-black text-slate-400 uppercase tracking-widest italic overflow-hidden">
                                       <span className="truncate max-w-[150px]">{r.filename}</span>
                                       <div className="w-1 h-1 bg-slate-200 rounded-full shrink-0" />
                                       <span className="flex items-center gap-1 shrink-0"><Zap size={10} className="text-blue-500 fill-blue-500"/> {r.usage_count || 0} Uses</span>
                                       <div className="w-1 h-1 bg-slate-200 rounded-full shrink-0" />
                                       <span className="shrink-0">{new Date(r.created_at).toLocaleDateString()}</span>
                                    </div>
                                 </div>
                              </div>
                              
                              <div className="flex items-center gap-3 mt-6 md:mt-0 opacity-100 lg:opacity-0 group-hover:opacity-100 transition-all duration-500 shrink-0">
                                 <button className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-blue-600 hover:border-blue-400 transition-all shadow-sm" onClick={() => handleManageFilters(r)} title="Manage Extraction Logic"><Search size={18} /></button>
                                 <button className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-slate-900 hover:border-slate-400 transition-all shadow-sm" onClick={() => handleDownloadResumeById(r.id, r.filename)} title="Download Asset"><Download size={18} /></button>
                                 {!r.is_active && (
                                    <>
                                       <button className="px-4 py-2 bg-blue-600 text-white rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-black transition-all shadow-xl active:scale-95 italic whitespace-nowrap" onClick={() => handleActivateResume(r.id)}>Set Active</button>
                                       <button className="p-3 bg-rose-50 border border-rose-100 rounded-2xl text-rose-400 hover:text-rose-600 hover:bg-rose-100 transition-all" onClick={() => handleDeleteResume(r)} title="Purge Asset"><Trash2 size={20} /></button>
                                    </>
                                 )}
                              </div>
                            </div>
                          ))
                      )}
                   </div>
                </div>
             </div>
          </div>
        )}

        {tab === 'letters' && (
          <div className="animate-in fade-in slide-in-from-right-4 duration-500">
             <div className="bg-white border border-slate-200 rounded-[2.5rem] p-8 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none rotate-12"><Mail size={140}/ ></div>
                <div className="flex items-center justify-between mb-8 relative z-10">
                   <div>
                      <h3 className="text-3xl font-black text-slate-900 tracking-tight italic flex items-center gap-3">
                         <Mail size={32} className="text-blue-600" /> Cover Letter Library
                      </h3>
                      <p className="text-slate-400 text-xs font-black uppercase tracking-widest mt-2">{letters.length} AI-Generated Cover Letters</p>
                   </div>
                   <button onClick={async () => { const r = await axiosInstance.get('/cover_letters'); setLetters(r.data.items || []); }} className="w-12 h-12 rounded-2xl bg-slate-50 text-slate-400 hover:text-blue-600 border border-slate-100 flex items-center justify-center transition-all hover:bg-white shadow-inner"><RefreshCw size={22}/></button>
                </div>

                {letters.length === 0 ? (
                  <div className="py-32 text-center border-4 border-dashed border-slate-50 rounded-[4rem] bg-slate-50/20">
                      <Mail size={64} className="mx-auto text-slate-100 mb-6" />
                      <p className="text-xs font-black text-slate-300 uppercase tracking-[0.3em] italic">No cover letters yet</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-10">
                    {letters.map((it) => (
                      <div key={it.id} className="flex items-center justify-between p-5 bg-slate-50/50 border border-slate-100 rounded-2xl group hover:bg-white hover:border-blue-400 hover:shadow-2xl hover:shadow-blue-500/5 transition-all duration-500">
                        <div className="flex items-center gap-6 flex-1 min-w-0">
                           <div className="w-12 h-12 bg-white border border-slate-200 text-blue-500 rounded-xl flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-all shadow-inner group-hover:scale-110">
                              <FileText size={20} />
                           </div>
                           <div className="min-w-0">
                             <div className="font-black text-slate-900 text-sm tracking-tight italic uppercase truncate">{it.title || 'COVER LETTER'} {it.company ? `@ ${it.company}` : ''}</div>
                             <div className="text-[10px] text-slate-400 font-black uppercase tracking-widest mt-1 italic">{new Date(it.created_at).toLocaleDateString()}</div>
                           </div>
                        </div>
                        <button 
                          className="p-2.5 bg-white border border-slate-200 text-slate-400 hover:text-slate-900 rounded-xl transition-all shadow-sm group-hover:shadow-lg" 
                          onClick={async () => { 
                            try { const resp = await axiosInstance.get(`/cover_letters/download?id=${encodeURIComponent(it.id)}`, { responseType: 'blob' }); const blob = new Blob([resp.data], { type: resp.headers['content-type'] || 'application/pdf' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = it.filename || 'letter.pdf'; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url); } catch {} 
                          }}
                        >
                          <Download size={20} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
             </div>
          </div>
        )}

        {selectedResumeForFilters && (
          <ResumeFiltersModal
            resumeId={selectedResumeForFilters.id}
            resumeTitle={selectedResumeForFilters.title || selectedResumeForFilters.filename}
            isOpen={filtersModalOpen}
            onClose={() => { setFiltersModalOpen(false); setSelectedResumeForFilters(null); }}
            onUpdate={handleFiltersUpdate}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
